import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client/react";
import { Building2, ChevronDown, Star, X, ArrowUp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { SWITCH_ORGANIZATION, SET_DEFAULT_ORGANIZATION } from "@/graphql/mutations";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getScopedStorageItem, removeScopedStorageItem, setScopedStorageItem } from "@/lib/session-utils";
import { saveTokens } from "@/lib/token-utils";

interface Organization {
  id: string;
  name: string;
  role: string;
  isPersonal?: boolean;
}

interface SwitchOrganizationResponse {
  switchOrganization: {
    success: boolean;
    organization?: {
      id: string;
      name: string;
      role: string;
      isPersonal: boolean;
      joinedAt: string;
    };
    tokens?: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    };
    error?: string;
  };
}

interface SetDefaultOrganizationResponse {
  setDefaultOrganization: {
    success: boolean;
    error?: string;
  };
}

interface OrganizationSwitcherProps {
  organizations: Organization[];
  currentOrganizationId?: string;
  className?: string;
}

export function OrganizationSwitcher({
  organizations,
  currentOrganizationId,
  className,
}: OrganizationSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const [defaultOrgId, setDefaultOrgId] = useState<string | null>(() => {
    return getScopedStorageItem('defaultOrganizationId');
  });
  
  // Onboarding tooltip for new users who just joined an org
  const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
  
  useEffect(() => {
    const shouldShow = getScopedStorageItem('showOrgSwitcherTooltip') === 'true';
    if (shouldShow && organizations.length > 1) {
      const timer = setTimeout(() => {
        setShowOnboardingTooltip(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [organizations.length]);
  
  const dismissOnboardingTooltip = () => {
    setShowOnboardingTooltip(false);
    removeScopedStorageItem('showOrgSwitcherTooltip');
  };
  
  const [switchOrganization, { loading: switchLoading }] = useMutation<SwitchOrganizationResponse>(SWITCH_ORGANIZATION, {
    onCompleted: (data) => {
      if (data.switchOrganization.success) {
        const { tokens, organization } = data.switchOrganization;
        if (tokens) {
          saveTokens(tokens.accessToken, tokens.refreshToken);
        }
        if (organization?.id) {
          setScopedStorageItem('currentOrganizationId', organization.id);
        }
        toast.success(`Switched to ${organization?.name || 'organization'}`);
        window.location.reload();
      } else {
        toast.error(data.switchOrganization.error || 'Failed to switch organization');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to switch organization');
    },
  });

  // Track which org we're setting as default (for optimistic UI)
  const [pendingDefaultOrgId, setPendingDefaultOrgId] = useState<string | null>(null);
  
  const [setDefaultOrganization, { loading: defaultLoading }] = useMutation<SetDefaultOrganizationResponse>(SET_DEFAULT_ORGANIZATION, {
    onCompleted: (data) => {
      if (data.setDefaultOrganization.success && pendingDefaultOrgId) {
        setDefaultOrgId(pendingDefaultOrgId);
        setScopedStorageItem('defaultOrganizationId', pendingDefaultOrgId);
        toast.success('Default workspace updated');
      } else {
        toast.error(data.setDefaultOrganization.error || 'Failed to set default organization');
      }
      setPendingDefaultOrgId(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to set default organization');
      setPendingDefaultOrgId(null);
    },
  });

  // Don't show switcher if user has only one organization
  if (!organizations || organizations.length <= 1) {
    const currentOrg = organizations?.[0];
    if (!currentOrg) return null;
    
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">
          {currentOrg.name}
        </span>
      </div>
    );
  }

  const currentOrg = organizations.find(org => org.id === currentOrganizationId) || organizations[0];
  const isLoading = switchLoading || defaultLoading;

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrganizationId) return;
    
    await switchOrganization({
      variables: { organizationId: orgId },
    });
    setIsOpen(false);
  };

  const handleSetDefault = async (e: React.MouseEvent, orgId: string) => {
    e.stopPropagation();
    if (orgId === defaultOrgId) return;
    setPendingDefaultOrgId(orgId);
    await setDefaultOrganization({
      variables: { organizationId: orgId },
    });
  };

  // Get first letter for avatar
  const getInitial = (name: string) => name.charAt(0).toUpperCase();

  return (
    <div className="relative">
      {/* Onboarding Tooltip */}
      {showOnboardingTooltip && (
        <div className="absolute top-full left-0 mt-3 z-50 animate-in fade-in-0 slide-in-from-top-2 duration-300">
          <div className="relative">
            {/* Arrow pointing up */}
            <div className="absolute -top-2 left-6">
              <ArrowUp className="w-5 h-5 text-primary fill-primary" />
            </div>
            <div className="bg-primary text-primary-foreground rounded-lg shadow-xl p-4 max-w-[280px]">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1">
                  <p className="font-semibold text-sm">Switch Workspaces</p>
                  <p className="text-xs opacity-90">
                    You now belong to multiple workspaces! Click here anytime to switch between them.
                  </p>
                </div>
                <button
                  onClick={dismissOnboardingTooltip}
                  className="shrink-0 p-1 rounded-md hover:bg-white/20 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={() => {
                  dismissOnboardingTooltip();
                  setIsOpen(true);
                }}
                className="mt-3 w-full text-xs font-medium bg-white/20 hover:bg-white/30 rounded-md py-1.5 px-3 transition-colors"
              >
                Got it, show me
              </button>
            </div>
          </div>
        </div>
      )}
      
      <DropdownMenu open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        // Dismiss tooltip when user opens the dropdown
        if (open && showOnboardingTooltip) {
          dismissOnboardingTooltip();
        }
      }}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "flex items-center gap-2 h-8 px-2 hover:bg-muted/60",
              showOnboardingTooltip && "ring-2 ring-primary ring-offset-2 ring-offset-background",
              className
            )}
            disabled={isLoading}
          >
            <div className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-semibold text-primary">
                {getInitial(currentOrg?.name || 'O')}
              </span>
            </div>
            <span className="text-sm font-medium max-w-[140px] truncate">
              {currentOrg?.name || 'Select Organization'}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-1">
        {organizations.map((org) => {
          const isSelected = org.id === currentOrganizationId;
          const isDefault = org.id === defaultOrgId;
          return (
            <div
              key={org.id}
              onClick={() => handleSwitch(org.id)}
              className={cn(
                "flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer transition-colors",
                isSelected 
                  ? "bg-primary/10 border border-primary/20" 
                  : "hover:bg-muted/60"
              )}
            >
              {/* Avatar */}
              <div className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center shrink-0",
                isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                <span className="text-sm font-semibold">
                  {getInitial(org.name)}
                </span>
              </div>
              
              {/* Name & Role */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm font-medium truncate",
                  isSelected && "text-primary"
                )}>
                  {org.name}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {org.role.toLowerCase()}
                </p>
              </div>
              
              {/* Set Default Button */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => handleSetDefault(e, org.id)}
                    disabled={isDefault}
                    className={cn(
                      "p-1.5 rounded-md transition-colors shrink-0",
                      isDefault 
                        ? "text-amber-500 cursor-default" 
                        : "text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10"
                    )}
                  >
                    <Star className={cn("w-4 h-4", isDefault && "fill-amber-500")} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  {isDefault ? "Default workspace" : "Set as default"}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default OrganizationSwitcher;
